import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import { baseRole, getErrorMessage } from "../../../../common";
import { ENROLLMENT_STATUS } from "../../../../constants";
import CourseLevelsService from "../../../../services/campus/courseLevels.service";
import EmployeesService from "../../../../services/campus/employees.service";
import PanelDeNivel from "../../portalClase/contenido/panelesDeNivel";
import { BarraFiltros, BuscadorBarra, coincide } from "../../../../components/buscadorBarra/buscadorBarra";
import { cargarKits, KitOpcion } from "./cursosApi";
import { Chip, Dato, nombreMaestra, Seccion, Ventana } from "./piezas";

/*
	Ventana del módulo (Horarios Claros, 2026-11-01): Datos, Maestras, Kits, Contenido y Evaluación en una sola ventana, con un solo
	«Guardar». Guarda con el mismo PUT/POST v1 de siempre (CourseLevelsService.saveLevel): las maestras y los kits viajan como listas
	de ids por agregar y por quitar (kits.idsAdding / kits.idsDeletion, que el servidor ya sabía leer). Las tarifas por moneda viejas
	ya no se ven, pero se mandan tal cual estaban: guardar no las cambia.
*/

export interface PermisosModulo { read: boolean; create: boolean; update: boolean; delete: boolean }

interface Props {
	campusId: number;
	curso: any;
	nivel: any | null;
	vivos: number | null;
	permisos: PermisosModulo;
	puedeNuevoHorario: boolean;
	puedeContenido: boolean;
	onClose: () => void;
	/** Recarga la lista; si es un módulo nuevo la ventana se cierra sola. */
	onChanged: (texto: string, cerrar: boolean) => Promise<void>;
}

type Pestana = "datos" | "maestras" | "kits" | "contenido" | "evaluacion";

const CAMPOS_NUEVO = { name: "", code: "", status_id: 1, session_min_hours: "1", week_hours: "1", week_min_days: "1", week_max_days: "1", description: "" };
const CAMPOS_PRECIO = [
	"subscription_base_price_mxn", "subscription_base_price_hnl", "subscription_base_price_usd",
	"monthly_billing_base_price_mxn", "monthly_billing_base_price_hnl", "monthly_billing_base_price_usd",
	"subscription_surcharge_base_cost_mxn", "subscription_surcharge_base_cost_hnl", "subscription_surcharge_base_cost_usd",
	"monthly_billing_surcharge_base_cost_mxn", "monthly_billing_surcharge_base_cost_hnl", "monthly_billing_surcharge_base_cost_usd",
	"second_surcharge_base_cost_mxn", "second_surcharge_base_cost_hnl", "second_surcharge_base_cost_usd"
];

const borradorDe = (n: any | null) => n
	? { name: n.name || "", code: n.code || "", status_id: Number(n.status_id), session_min_hours: String(n.session_min_hours ?? ""), week_hours: String(n.week_hours ?? ""),
		week_min_days: String(n.week_min_days ?? ""), week_max_days: String(n.week_max_days ?? ""), description: n.description || "" }
	: { ...CAMPOS_NUEVO };

const ModuloVentana: React.FC<Props> = ({ campusId, curso, nivel, vivos, permisos, puedeNuevoHorario, puedeContenido, onClose, onChanged }) => {
	const navigate = useNavigate();
	const yo: any = useSelector((state: RootState) => state.userProfile.value);
	const esNuevo = nivel === null;
	const puedeEditar = esNuevo ? permisos.create : permisos.update;
	const [pestana, setPestana] = useState<Pestana>("datos");
	const [borrador, setBorrador] = useState(borradorDe(nivel));
	const [agregarMaestras, setAgregarMaestras] = useState<{ [id: number]: any }>({});
	const [quitarMaestras, setQuitarMaestras] = useState<{ [id: number]: boolean }>({});
	const [agregarKits, setAgregarKits] = useState<{ [id: number]: KitOpcion }>({});
	const [quitarKits, setQuitarKits] = useState<{ [id: number]: boolean }>({});
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmando, setConfirmando] = useState<boolean>(false);

	// Al recargarse el módulo (después de guardar) se parte de lo que quedó en el servidor.
	useEffect(() => {
		setBorrador(borradorDe(nivel)); setAgregarMaestras({}); setQuitarMaestras({}); setAgregarKits({}); setQuitarKits({}); setError(null);
	}, [nivel]);

	const original = useMemo(() => borradorDe(nivel), [nivel]);
	const hayCambios = esNuevo ? borrador.name.trim() !== "" : (
		JSON.stringify(borrador) !== JSON.stringify(original) || Object.keys(agregarMaestras).length > 0 || Object.keys(quitarMaestras).length > 0
		|| Object.keys(agregarKits).length > 0 || Object.keys(quitarKits).length > 0);

	const cerrar = () => {
		if (hayCambios && !esNuevo && !window.confirm("Hay cambios sin guardar. ¿Cerrar de todos modos?")) return;
		onClose();
	};

	const poner = (k: string, v: any) => setBorrador((b) => ({ ...b, [k]: v }));

	// ---- maestras
	const [maestras, setMaestras] = useState<any[] | null>(null);
	const [qMaestra, setQMaestra] = useState<string>("");
	const [errorMaestras, setErrorMaestras] = useState<string | null>(null);
	useEffect(() => {
		if (pestana !== "maestras" || !puedeEditar) return;
		let vigente = true;
		setErrorMaestras(null);
		EmployeesService.searchEmployees({ q: qMaestra, roleIds: [baseRole.MENTOR], statusId: 1 }, undefined, 100)
			.then((r: any) => { if (vigente) setMaestras(Array.isArray(r.data) ? r.data : []); })
			.catch((e: any) => { if (vigente) { setMaestras([]); setErrorMaestras(getErrorMessage(e)); } });
		return () => { vigente = false; };
	}, [pestana, qMaestra, puedeEditar]);

	const maestrasActuales: any[] = (nivel?.mentors || []);
	const idsMaestras = new Set<number>(maestrasActuales.map((m: any) => Number(m.id)));
	const maestrasVigentes: any[] = [...maestrasActuales.filter((m: any) => !quitarMaestras[m.id]), ...Object.keys(agregarMaestras).map((id) => agregarMaestras[Number(id)])];
	// Una persona que es alumna activa de este mismo módulo no puede ser su maestra.
	const esAlumnaDelModulo = (m: any): boolean => !esNuevo && (m.enrollments || []).some((e: any) => e?.schedule?.level_id === nivel.id && e.status_id === ENROLLMENT_STATUS.ACTIVE);
	const maestrasPorAgregar = (maestras || []).filter((m: any) => m.id !== yo?.id && (!idsMaestras.has(Number(m.id)) || quitarMaestras[m.id]) && !agregarMaestras[m.id]);

	const agregarMaestra = (m: any) => {
		if (idsMaestras.has(Number(m.id))) setQuitarMaestras((q) => { const n = { ...q }; delete n[m.id]; return n; });
		else setAgregarMaestras((a) => ({ ...a, [m.id]: m }));
	};
	const quitarMaestra = (m: any) => {
		if (agregarMaestras[m.id]) setAgregarMaestras((a) => { const n = { ...a }; delete n[m.id]; return n; });
		else setQuitarMaestras((q) => ({ ...q, [m.id]: true }));
	};

	// ---- kits
	const [kits, setKits] = useState<KitOpcion[] | null>(null);
	const [qKit, setQKit] = useState<string>("");
	const [errorKits, setErrorKits] = useState<string | null>(null);
	useEffect(() => {
		if (pestana !== "kits" || !puedeEditar || kits !== null) return;
		let vigente = true;
		cargarKits(campusId).then((k) => { if (vigente) setKits(k); }).catch((e) => { if (vigente) { setKits([]); setErrorKits(getErrorMessage(e)); } });
		return () => { vigente = false; };
	}, [pestana, puedeEditar, kits, campusId]);

	const kitsActuales: any[] = (nivel?.kits || []);
	const idsKits = new Set<number>(kitsActuales.map((k: any) => Number(k.id)));
	const kitsVigentes: { id: number; name: string; sku: string | null; nuevo: boolean }[] = [
		...kitsActuales.filter((k: any) => !quitarKits[k.id]).map((k: any) => ({ id: Number(k.id), name: String(k.name), sku: k.sku ?? null, nuevo: false })),
		...Object.keys(agregarKits).map((id) => ({ ...agregarKits[Number(id)], nuevo: true }))
	];
	const kitsQuitados: any[] = kitsActuales.filter((k: any) => quitarKits[k.id]);
	const kitsPorAgregar = (kits || []).filter((k) => (!idsKits.has(k.id) || quitarKits[k.id]) && !agregarKits[k.id] && coincide(qKit, k.name, k.sku));

	const agregarKit = (k: KitOpcion) => {
		if (idsKits.has(k.id)) setQuitarKits((q) => { const n = { ...q }; delete n[k.id]; return n; });
		else setAgregarKits((a) => ({ ...a, [k.id]: k }));
	};
	const quitarKit = (id: number) => {
		if (agregarKits[id]) setAgregarKits((a) => { const n = { ...a }; delete n[id]; return n; });
		else setQuitarKits((q) => ({ ...q, [id]: true }));
	};

	// ---- guardar / borrar
	const validar = (): string | null => {
		if (borrador.name.trim() === "") return "Falta el nombre del módulo.";
		const sesion = Number(borrador.session_min_hours), semana = Number(borrador.week_hours), min = Number(borrador.week_min_days), max = Number(borrador.week_max_days);
		if (!(sesion >= 1 && sesion <= 15)) return "Las horas por sesión van de 1 a 15.";
		if (!(semana >= 1 && semana <= 105)) return "Las horas por semana van de 1 a 105.";
		if (!(min >= 1 && min <= 6) || !Number.isInteger(min)) return "Los días mínimos van de 1 a 6.";
		if (!(max >= 1 && max <= 7) || !Number.isInteger(max)) return "Los días máximos van de 1 a 7.";
		if (max < min) return "Los días máximos no pueden ser menos que los mínimos.";
		return null;
	};

	const guardar = async () => {
		const problema = validar();
		if (problema) { setError(problema); setPestana("datos"); return; }
		const mentores: any = {}, kitsP: any = {};
		if (Object.keys(agregarMaestras).length) mentores.idsAdding = Object.keys(agregarMaestras).join(",");
		if (Object.keys(quitarMaestras).length) mentores.idsDeletion = Object.keys(quitarMaestras).join(",");
		if (Object.keys(agregarKits).length) kitsP.idsAdding = Object.keys(agregarKits).join(",");
		if (Object.keys(quitarKits).length) kitsP.idsDeletion = Object.keys(quitarKits).join(",");
		// Un módulo nuevo parte de tarifas viejas en cero (como siempre); uno que ya existe manda las suyas sin tocarlas.
		const base: any = esNuevo ? Object.fromEntries(CAMPOS_PRECIO.map((c) => [c, 0])) : { ...nivel };
		const editado = {
			...base, id: esNuevo ? null : nivel.id, course_id: curso.id, name: borrador.name.trim(), code: borrador.code,
			status_id: borrador.status_id, session_min_hours: borrador.session_min_hours, week_hours: borrador.week_hours,
			week_min_days: borrador.week_min_days, week_max_days: borrador.week_max_days, description: borrador.description
		};
		setBusy(true); setError(null);
		try {
			// Las actividades no se tocan desde aquí: una lista vacía deja las ligadas como están.
			await CourseLevelsService.saveLevel({ ...(nivel || {}), course_id: curso.id }, editado, mentores, kitsP, []);
			await onChanged(esNuevo ? "Módulo creado" : "Módulo guardado", esNuevo);
		} catch (e: any) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const borrar = async () => {
		setBusy(true); setError(null);
		try {
			await CourseLevelsService.removeLevels(campusId, [nivel.id]);
			await onChanged("Módulo borrado", true);
		} catch (e: any) {
			setError(getErrorMessage(e));
			setConfirmando(false);
		} finally {
			setBusy(false);
		}
	};

	const irNuevoHorario = () => {
		if (hayCambios && !window.confirm("Hay cambios sin guardar. ¿Ir a Horarios de todos modos?")) return;
		navigate("/schedules?nuevo=1&nivel=" + nivel.id);
	};

	const tabs: { k: Pestana; t: string; n?: number }[] = [
		{ k: "datos", t: "Datos" },
		{ k: "maestras", t: "Maestras", n: maestrasVigentes.length },
		{ k: "kits", t: "Kits", n: kitsVigentes.length },
		...(puedeContenido && !esNuevo ? [{ k: "contenido" as Pestana, t: "Contenido" }, { k: "evaluacion" as Pestana, t: "Evaluación" }] : [])
	];

	const campo = (k: string, etiqueta: string, tipo: "text" | "number" = "text", extra: any = {}) => (
		<label className="wau-campo">
			<span className="wau-lab">{etiqueta}</span>
			<input className="wau-entrada" type={tipo} value={(borrador as any)[k]} disabled={!puedeEditar || busy} onChange={(e) => poner(k, e.target.value)} {...extra} />
		</label>
	);

	const pie = (
		<>
			{!esNuevo && puedeNuevoHorario ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={irNuevoHorario} disabled={busy}><FontAwesomeIcon icon={solid("plus")} />&nbsp;Nuevo Horario</button> : null}
			<span style={{ flex: 1 }} />
			{confirmando ? <>
				<span style={{ fontSize: 14 }}>¿Borrar este módulo?</span>
				<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setConfirmando(false)}>Cancelar</button>
				<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={borrar}>Sí, Borrar</button>
			</> : <>
				{!esNuevo && permisos.delete ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setConfirmando(true)}>Borrar Módulo</button> : null}
				{puedeEditar ? <button type="button" className="btn btn-primary btn-sm" disabled={busy || !hayCambios} onClick={guardar}>{busy ? "Guardando" : esNuevo ? "Crear Módulo" : "Guardar"}</button> : null}
			</>}
		</>
	);

	return (
		<Ventana onClose={cerrar} busy={busy} pie={pie} titulo={<>
			<span>{esNuevo ? "Nuevo Módulo" : nivel.name}</span>
			<Chip>{curso.name}</Chip>
			{!esNuevo ? <Chip tono={Number(nivel.status_id) === 1 ? "acento" : ""}>{Number(nivel.status_id) === 1 ? "Activo" : "Inactivo"}</Chip> : null}
		</>}>
			{error ? <div className="alert alert-danger" style={{ fontSize: 14.5 }}>{error}</div> : null}
			<ul className="nav nav-pills waTabs wcuTabs" style={{ gap: 4, marginTop: 0 }}>
				{tabs.map((x) => (
					<li className="nav-item" key={x.k}>
						<button type="button" className={"nav-link btn btn-sm " + (pestana === x.k ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => setPestana(x.k)}>
							{x.t}{x.n ? <span className="waTabCuenta">{x.n}</span> : null}
						</button>
					</li>
				))}
			</ul>

			{pestana === "datos" ? <>
				<div className="wcuForm">
					<div className="ancho">{campo("name", "Nombre", "text", { maxLength: 50 })}</div>
					<label className="wau-campo">
						<span className="wau-lab">Código</span>
						<input className="wau-entrada" type="text" maxLength={50} value={esNuevo ? "" : borrador.code} placeholder={esNuevo ? "Se genera al guardar" : ""} disabled={!puedeEditar || busy || esNuevo} onChange={(e) => poner("code", e.target.value)} />
					</label>
					<label className="wau-campo">
						<span className="wau-lab">Estado</span>
						<select className="wau-entrada" value={borrador.status_id} disabled={!puedeEditar || busy} onChange={(e) => poner("status_id", Number(e.target.value))}>
							<option value={1}>Activo</option><option value={0}>Inactivo</option>
						</select>
					</label>
					{campo("session_min_hours", "Horas por Sesión", "number", { step: 0.5, min: 1, max: 15 })}
					{campo("week_hours", "Horas por Semana", "number", { step: 0.5, min: 1, max: 105 })}
					{campo("week_min_days", "Días Mínimos por Semana", "number", { step: 1, min: 1, max: 6 })}
					{campo("week_max_days", "Días Máximos por Semana", "number", { step: 1, min: 1, max: 7 })}
					<label className="wau-campo ancho">
						<span className="wau-lab">Descripción</span>
						<textarea className="wau-entrada" maxLength={255} value={borrador.description} disabled={!puedeEditar || busy} onChange={(e) => poner("description", e.target.value)} />
					</label>
				</div>
				{!esNuevo ? <Seccion t="Horarios">
					<div className="wcuDatos">
						<Dato t="Horarios Vivos">{vivos === null ? "…" : vivos}</Dato>
						<Dato t="Maestras">{maestrasVigentes.length}</Dato>
						<Dato t="Kits">{kitsVigentes.length}</Dato>
					</div>
				</Seccion> : null}
			</> : null}

			{pestana === "maestras" ? <>
				{errorMaestras ? <div className="wcuAvisoOro">{errorMaestras}</div> : null}
				<div className="wcuLiga">
					{!maestrasVigentes.length ? <div className="wcuVacio">Sin maestras ligadas.</div> : maestrasVigentes.map((m: any) => (
						<div key={m.id} className={"wcuLigaFila" + (agregarMaestras[m.id] ? " nuevo" : "")}>
							<span className="nom">{nombreMaestra(m)}</span>
							{puedeEditar ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => quitarMaestra(m)}>Quitar</button> : null}
						</div>
					))}
					{maestrasActuales.filter((m: any) => quitarMaestras[m.id]).map((m: any) => (
						<div key={"q" + m.id} className="wcuLigaFila quita"><span className="nom">{nombreMaestra(m)}</span>
							<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => agregarMaestra(m)}>Deshacer</button></div>
					))}
				</div>
				{puedeEditar ? <Seccion t="Agregar Maestra">
					<BarraFiltros><BuscadorBarra valor={qMaestra} onBuscar={setQMaestra} placeholder="Buscar maestra" /></BarraFiltros>
					<div className="wcuOpciones">
						{maestras === null ? <div className="loader" /> : !maestrasPorAgregar.length ? <div className="wcuVacio">Sin resultados.</div> : maestrasPorAgregar.map((m: any) => {
							const alumna = esAlumnaDelModulo(m);
							return (
								<button key={m.id} type="button" className="wcuOpcion" disabled={busy || alumna} onClick={() => agregarMaestra(m)}>
									<span>{nombreMaestra(m)}</span>{alumna ? <small>Es alumna de este módulo</small> : null}
								</button>
							);
						})}
					</div>
				</Seccion> : null}
			</> : null}

			{pestana === "kits" ? <>
				{errorKits ? <div className="wcuAvisoOro">{errorKits}</div> : null}
				<div className="wcuLiga">
					{!kitsVigentes.length ? <div className="wcuVacio"><span className="wcuOro">Sin Kit</span></div> : kitsVigentes.map((k) => (
						<div key={k.id} className={"wcuLigaFila" + (k.nuevo ? " nuevo" : "")}>
							<span className="nom">{k.name}{k.sku ? <small>{k.sku}</small> : null}</span>
							{puedeEditar ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => quitarKit(k.id)}>Quitar</button> : null}
						</div>
					))}
					{kitsQuitados.map((k: any) => (
						<div key={"q" + k.id} className="wcuLigaFila quita"><span className="nom">{k.name}</span>
							<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => agregarKit({ id: Number(k.id), name: String(k.name), sku: k.sku ?? null })}>Deshacer</button></div>
					))}
				</div>
				{puedeEditar ? <Seccion t="Agregar Kit">
					<BarraFiltros><BuscadorBarra valor={qKit} onBuscar={setQKit} placeholder="Buscar kit" /></BarraFiltros>
					<div className="wcuOpciones">
						{kits === null ? <div className="loader" /> : !kitsPorAgregar.length ? <div className="wcuVacio">Sin resultados.</div> : kitsPorAgregar.slice(0, 60).map((k) => (
							<button key={k.id} type="button" className="wcuOpcion" disabled={busy} onClick={() => agregarKit(k)}>
								<span>{k.name}</span>{k.sku ? <small>{k.sku}</small> : null}
							</button>
						))}
					</div>
				</Seccion> : null}
			</> : null}

			{pestana === "contenido" && !esNuevo ? <PanelDeNivel key="contenido" pestana="contenido" nivel="modulo" id={nivel.id} /> : null}
			{pestana === "evaluacion" && !esNuevo ? <PanelDeNivel key="evaluacion" pestana="evaluacion" nivel="modulo" id={nivel.id} /> : null}
		</Ventana>
	);
};

export default ModuloVentana;
